import { AppBar, Avatar, Box, Container, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography, Chip, Divider, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { AccountCircle, Logout, AccessTime, Business } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import axios from "../api/axios";
import { getImageUrl } from '../utils/imageUtils';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const Header = ({ drawerWidth, onMenuClick }) => {
    const theme = useTheme();
    const isSoulTech = theme.palette.primary.main === '#2A9D8F';
    
    const [userMenu, setUserMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const { auth, setAuth } = useAuth();
    const [customerDb, setCustomerDb] = useState(auth?.customerDatabase || null);
    const [userAvatar, setUserAvatar] = useState(null);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [logoError, setLogoError] = useState(false);
    const axiosPrivate = useAxiosPrivate();
    const [companyName, setCompanyName] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());

    // Resetta logoLoaded quando customerDb cambia
    useEffect(() => {
        setLogoLoaded(false);
        setLogoError(false);
    }, [customerDb]);

    // Orologio in tempo reale
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Carica nome struttura
    useEffect(() => {
        async function fetchTenantSettings() {
            try {
                const res = await axiosPrivate.get('/tenant-settings');
                if (res.data?.company_name) {
                    setCompanyName(res.data.company_name);
                }
            } catch (err) {
                console.error('Errore caricamento tenant settings:', err);
            }
        }
        fetchTenantSettings();
    }, [axiosPrivate]);

    useEffect(() => {
        async function fetchDb() {
            if (!auth?.customerDatabase && auth?.email) {
                try {
                    const res = await axios.get(`/auth/customer-db?email=${encodeURIComponent(auth.email)}`);
                    setCustomerDb(res.data.database);
                } catch (err) {
                    setCustomerDb(null);
                }
            } else if (auth?.customerDatabase) {
                setCustomerDb(auth.customerDatabase);
            }
        }
        fetchDb();
    }, [auth]);

    useEffect(() => {
        async function fetchUserAvatar() {
            if (auth?.email) {
                try {
                    const res = await axiosPrivate.get('/auth/profile');
                    if (res.data.avatar) {
                        // Aggiungi timestamp per cache busting
                        const timestamp = new Date().getTime();
                        setUserAvatar(`${getImageUrl(res.data.avatar)}?t=${timestamp}`);
                    }
                } catch (err) {
                    console.error('Errore caricamento avatar:', err);
                    setUserAvatar(null);
                }
            }
        }
        fetchUserAvatar();
    }, [auth?.email, axiosPrivate]);

    const handleOpenUserMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setUserMenu(true);
    };

    const handleCloseUserMenu = () => {
        setAnchorEl(null);
        setUserMenu(false);
    };

    const handleMenuClick = (setting) => {
        handleCloseUserMenu();
        
        switch(setting) {
            case 'Account':
                navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/account`);
                break;
            case 'Logout':
                handleLogout();
                break;
            default:
                break;
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post('/auth/logout');
            setAuth({});
            navigate('/login');
        } catch (error) {
            // Logout locale anche se il server non risponde
            setAuth({});
            navigate('/login');
        }
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                width: '100%',
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Container maxWidth="100%">
                <Toolbar sx={{ justifyContent: "space-between", minHeight: 96, py: 2 }} disableGutters>
                    {/* Logo e Menu mobile */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {/* Icona del menu per dispositivi mobili */}
                        <Box sx={{ display: { xs: "block", sm: "none" } }}>
                            <IconButton
                                size="large"
                                aria-label="menu"
                                onClick={onMenuClick}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>

                        {/* Logo */}
                        {customerDb && !logoError ? (
                            <img 
                                key={customerDb}
                                src={getImageUrl('logos/logo.png', customerDb.match(/\d+$/)?.[0] || '001')}
                                alt="Logo" 
                                style={{ 
                                    width: 60, 
                                    height: 60,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    display: logoLoaded ? 'block' : 'none',
                                    border: isSoulTech ? '2px solid rgba(42, 157, 143, 0.5)' : 'none',
                                    boxShadow: isSoulTech ? '0 2px 12px rgba(42, 157, 143, 0.3)' : 'none'
                                }}
                                onLoad={() => setLogoLoaded(true)}
                                onError={() => {
                                    console.error('Logo non trovato');
                                    setLogoError(true);
                                }}
                            />
                        ) : (
                            <Box 
                                sx={{ 
                                    width: 60, 
                                    height: 60, 
                                    borderRadius: '50%', 
                                    bgcolor: isSoulTech ? 'rgba(42, 157, 143, 0.2)' : 'secondary.main',
                                    border: isSoulTech ? '2px solid rgba(42, 157, 143, 0.5)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Business sx={{ fontSize: 32, color: isSoulTech ? '#2A9D8F' : 'secondary.contrastText' }} />
                            </Box>
                        )}

                        {/* Nome Struttura - stile professionale */}
                        {companyName && (
                            <Box sx={{ 
                                display: { xs: 'none', md: 'flex' }, 
                                alignItems: 'center', 
                                ml: 2,
                                pl: 2,
                                borderLeft: `3px solid ${theme.palette.primary.main}`,
                            }}>
                                <Typography 
                                    component="h1"
                                    sx={{ 
                                        fontWeight: 600,
                                        fontSize: '1.35rem',
                                        letterSpacing: '0.03em',
                                        color: 'inherit',
                                        fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {companyName}
                                </Typography>
                            </Box>
                        )}

                        {/* Versione mobile */}
                        {companyName && (
                            <Box sx={{ 
                                display: { xs: 'flex', md: 'none' }, 
                                alignItems: 'center', 
                                ml: 1,
                                pl: 1,
                                borderLeft: `2px solid ${theme.palette.primary.main}`,
                                maxWidth: '45vw',
                                overflow: 'hidden',
                            }}>
                                <Typography 
                                    component="h1"
                                    noWrap
                                    sx={{ 
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        letterSpacing: '0.02em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {companyName}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Centro: Data e Ora - spostato a destra */}
                    <Box sx={{ 
                        display: { xs: 'none', sm: 'flex' }, 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        px: 3,
                        py: 1,
                        ml: 'auto',  // Sposta a destra
                        mr: 2,       // Margine dal menu utente
                        borderRadius: 2,
                        backgroundColor: isSoulTech ? 'rgba(42, 157, 143, 0.15)' : 'transparent',
                        border: isSoulTech ? '1px solid rgba(42, 157, 143, 0.25)' : 'none'
                    }}>
                        <Typography 
                            variant="h5" 
                            sx={{ 
                                fontWeight: 700, 
                                fontFamily: 'monospace',
                                letterSpacing: 2,
                                color: isSoulTech ? '#2A9D8F' : 'inherit',
                                textShadow: isSoulTech ? '0 0 10px rgba(42, 157, 143, 0.3)' : 'none'
                            }}
                        >
                            {currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                opacity: isSoulTech ? 1 : 0.85,
                                textTransform: 'capitalize',
                                color: isSoulTech ? 'rgba(255,255,255,0.85)' : 'inherit'
                            }}
                        >
                            {currentTime.toLocaleDateString('it-IT', { 
                                weekday: 'long', 
                                day: 'numeric', 
                                month: 'long' 
                            })}
                        </Typography>
                    </Box>

                    {/* Menu utente */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="Account">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <Avatar 
                                    alt="User Avatar" 
                                    src={userAvatar}
                                    sx={{ 
                                        bgcolor: isSoulTech ? '#2A9D8F' : 'secondary.main', 
                                        width: 44, 
                                        height: 44,
                                        border: isSoulTech ? '2px solid rgba(255,255,255,0.3)' : 'none',
                                        boxShadow: isSoulTech ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
                                    }}
                                >
                                    {!userAvatar && auth?.email ? auth.email.charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: "45px" }}
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={userMenu}
                            onClose={handleCloseUserMenu}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1, px: 2 }}>
                                <Avatar 
                                    alt="User Avatar" 
                                    src={userAvatar}
                                    sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}
                                >
                                    {!userAvatar && auth?.email ? auth.email.charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500 }}>
                                    {auth?.email || 'Utente non loggato'}
                                </Typography>
                            </Box>
                            <Divider />
                            <MenuItem onClick={() => handleMenuClick('Account')}>
                                <AccountCircle sx={{ mr: 1 }} />
                                <Typography textAlign="center">Account</Typography>
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuClick('Logout')}>
                                <Logout sx={{ mr: 1 }} />
                                <Typography textAlign="center">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Header;